import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Step, CallBackProps, STATUS, EVENTS, ACTIONS } from 'react-joyride';

const TOUR_KEY = 'welcome-tour-v2-completed';
const WELCOME_KEY = 'welcome-modal-shown';

export const useDashboardTour = () => {
  const { t } = useTranslation();
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [showWelcome, setShowWelcome] = useState(false);

  useEffect(() => {
    const tourCompleted = localStorage.getItem(TOUR_KEY);
    const welcomeShown = localStorage.getItem(WELCOME_KEY);
    if (!tourCompleted && !welcomeShown) {
      const timer = setTimeout(() => setShowWelcome(true), 500);
      return () => clearTimeout(timer);
    }
  }, []);

  // Um passo por item da barra lateral (navigation.ts). Itens escondidos (módulo desligado)
  // são pulados sozinhos (TARGET_NOT_FOUND).
  const steps: Step[] = [
    {
      target: '[data-tour="nav-dashboard"]',
      content: t('tour.navDashboard', 'Your numbers at a glance: results, tips and shortcuts.'),
      title: t('tour.navDashboardTitle', 'Home'),
      placement: 'right',
      disableBeacon: true,
    },
    {
      target: '[data-tour="nav-social-media"]',
      content: t('tour.navSocialMedia', 'Posts for your social networks, images and videos. AI helps you write and you schedule.'),
      title: t('tour.navSocialMediaTitle', 'Create content'),
      placement: 'right',
      disableBeacon: true,
    },
    {
      target: '[data-tour="nav-blog-seo"]',
      content: t('tour.navBlogSeo', 'Escreva no blog (ou deixe o robô escrever) e apareça no Google quando as pessoas procurarem o que você vende.'),
      title: t('tour.navBlogSeoTitle', 'Blog e SEO'),
      placement: 'right',
      disableBeacon: true,
    },
    {
      target: '[data-tour="nav-landing-pages"]',
      content: t('tour.navLandingPages', 'Sales pages, a blog that shows up on Google and short links to share.'),
      title: t('tour.navLandingPagesTitle', 'Site & blog'),
      placement: 'right',
      disableBeacon: true,
    },
    {
      target: '[data-tour="nav-campaigns"]',
      content: t('tour.navCampaigns', 'Create and follow your ads on Google, Facebook and Instagram.'),
      title: t('tour.navCampaignsTitle', 'Ads'),
      placement: 'right',
      disableBeacon: true,
    },
    {
      target: '[data-tour="nav-contacts"]',
      content: t('tour.navContacts', 'Everyone who talked to your business, in one list.'),
      title: t('tour.navContactsTitle', 'Contacts'),
      placement: 'right',
      disableBeacon: true,
    },
    {
      target: '[data-tour="nav-workflows"]',
      content: t('tour.navWorkflows', 'Flows that work on their own: reply, send messages and alert you.'),
      title: t('tour.navWorkflowsTitle', 'Automations'),
      placement: 'right',
      disableBeacon: true,
    },
    {
      target: '[data-tour="nav-competitors"]',
      content: t('tour.navCompetitors', 'See what your competitors do and where you rank on Google.'),
      title: t('tour.navCompetitorsTitle', 'Competitors'),
      placement: 'right',
      disableBeacon: true,
    },
    {
      target: '[data-tour="nav-subscription"]',
      content: t('tour.navSubscription', 'Your plan, payments and usage limits.'),
      title: t('tour.navSubscriptionTitle', 'Plan'),
      placement: 'right',
      disableBeacon: true,
    },
    {
      target: '[data-tour="nav-settings"]',
      content: t('tour.navSettings', 'Your account, connections and notifications.'),
      title: t('tour.navSettingsTitle', 'Settings'),
      placement: 'right',
      disableBeacon: true,
    },
  ];

  const handleCallback = useCallback((data: CallBackProps) => {
    const { status, action, index, type } = data;

    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status as any)) {
      window.dispatchEvent(new CustomEvent('tour:sidebar', { detail: false }));
      setRun(false);
      setStepIndex(0);
      localStorage.setItem(TOUR_KEY, 'true');
      return;
    }

    if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      const nextIndex = index + (action === ACTIONS.PREV ? -1 : 1);
      setStepIndex(nextIndex);
    }
  }, []);

  const startTourFromWelcome = useCallback(() => {
    setShowWelcome(false);
    localStorage.setItem(WELCOME_KEY, 'true');
    // Delay to allow modal transition to complete
    // No celular o menu fica escondido: abre a gaveta para o tour mostrar os itens
    if (window.innerWidth < 1024) window.dispatchEvent(new CustomEvent('tour:sidebar', { detail: true }));
    setTimeout(() => {
      setStepIndex(0);
      setRun(true);
    }, 400);
  }, []);

  const skipWelcome = useCallback(() => {
    setShowWelcome(false);
    localStorage.setItem(WELCOME_KEY, 'true');
    localStorage.setItem(TOUR_KEY, 'true');
  }, []);

  const restartTour = useCallback(() => {
    localStorage.removeItem(TOUR_KEY);
    localStorage.removeItem(WELCOME_KEY);
    setStepIndex(0);
    setRun(false);
    setShowWelcome(true);
  }, []);

  return {
    run,
    steps,
    stepIndex,
    handleCallback,
    restartTour,
    showWelcome,
    startTourFromWelcome,
    skipWelcome,
  };
};
